import { Link } from 'react-router-dom';
import { mangaList, categories } from '@/mocks/manga';
import type { MangaItem } from '@/mocks/manga';

function MangaCard({ manga }: { manga: MangaItem }) {
  return (
    <Link
      to={`/manga/${manga.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={manga.coverImage}
          alt={manga.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${
            manga.status === '连载中'
              ? 'bg-primary-500 text-background-50'
              : 'bg-foreground-600 text-background-50'
          }`}>
            {manga.status}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
          <div className="flex items-center gap-1 text-background-50 text-xs">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-star-fill text-secondary-500"></i>
            </span>
            {manga.rating}
          </div>
        </div>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors duration-200 truncate font-heading">
          {manga.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1 truncate">{manga.author} · {manga.category}</p>
      </div>
    </Link>
  );
}

export default function HomePage() {
  const featuredManga = mangaList.filter((m) => m.rating >= 9.3).slice(0, 8);
  const latestManga = [...mangaList].sort((a, b) =>
    new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()
  ).slice(0, 8);
  const popularManga = [...mangaList].sort((a, b) => b.views - a.views).slice(0, 8);

  const faqs = [
    {
      question: '木瓜漫画是否完全免费？',
      answer: '是的！木瓜漫画所有漫画资源均为正版授权，对所有用户完全免费开放。无需付费，无需会员，随时随地畅享阅读乐趣。',
    },
    {
      question: '如何在木瓜漫画上找到想看的漫画？',
      answer: '您可以通过首页的分类导航浏览不同类别的漫画，也可以使用搜索功能直接搜索漫画名称。我们还会根据您的阅读偏好推荐相关漫画。',
    },
    {
      question: '木瓜漫画支持哪些设备阅读？',
      answer: '木瓜漫画完美适配电脑、平板和手机等各类设备。无论在什么设备上，都能获得优质的阅读体验。您还可以下载木瓜漫画APP，享受离线阅读功能。',
    },
    {
      question: '漫画更新频率是怎样的？',
      answer: '我们与各大漫画平台保持同步更新，热门漫画通常每周更新一到两话，确保您能第一时间阅读到最新内容。',
    },
    {
      question: '遇到问题如何联系客服？',
      answer: '您可以通过页面底部的联系方式与我们取得联系，客服邮箱 contact@muguamanhua.com，客服电话 400-888-6666，我们会在24小时内回复您的咨询。',
    },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '木瓜漫画',
            url: 'https://www.muguamanhua.com',
            description: '海量正版漫画免费在线阅读平台，提供热血、恋爱、悬疑、搞笑等多种类型漫画。',
            contactPoint: { '@type': 'ContactPoint', telephone: '400-888-6666', contactType: 'customer service', email: 'contact@muguamanhua.com' },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '木瓜漫画',
            url: 'https://www.muguamanhua.com',
            description: '海量正版漫画免费在线阅读平台',
            potentialAction: { '@type': 'SearchAction', target: 'https://www.muguamanhua.com/search?q={search_term_string}', 'query-input': 'required name=search_term_string' },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '木瓜漫画 - 海量正版漫画免费在线阅读平台',
            description: '木瓜漫画是一款深受漫画迷喜爱的在线漫画阅读平台，提供海量正版漫画资源。',
            url: 'https://www.muguamanhua.com/',
          }),
        }}
      />

      {/* Hero Section */}
      <section className="relative w-full h-[520px] md:h-[620px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=A%20warm%20artistic%20abstract%20background%20with%20flowing%20ink%20strokes%20in%20coral%20orange%20and%20warm%20peach%20tones%2C%20soft%20watercolor%20texture%2C%20Japanese%20ink%20painting%20style%2C%20clean%20and%20elegant%2C%20no%20text%2C%20gradient%20from%20warm%20cream%20to%20soft%20coral&width=1800&height=900&seq=manga-hero-bg-01&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/35"></div>

        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4 md:px-6">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 font-heading mb-4 leading-tight">
            海量正版漫画
            <br />
            免费在线阅读
          </h1>
          <p className="text-sm md:text-lg text-background-200 max-w-2xl mb-8 leading-relaxed">
            木瓜漫画汇集热血、恋爱、悬疑、搞笑等多种类型正版漫画，
            <br className="hidden md:block" />
            界面清爽、更新及时、完全免费，随时随地畅享漫画乐趣
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <Link
              to="/category/rexue"
              className="px-8 py-3 bg-primary-500 text-background-50 rounded-lg text-sm md:text-base font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              立即开始阅读
            </Link>
            <Link
              to="/about"
              className="px-8 py-3 bg-background-50/20 backdrop-blur-sm text-background-50 border border-background-50/30 rounded-lg text-sm md:text-base font-medium hover:bg-background-50/30 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              了解更多
            </Link>
          </div>

          <div className="flex items-center gap-8 mt-10">
            {[
              { value: '10000+', label: '正版漫画' },
              { value: '500万+', label: '活跃读者' },
              { value: '99.7%', label: '好评率' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-xl md:text-2xl font-bold text-background-50 font-heading">{stat.value}</div>
                <div className="text-xs md:text-sm text-background-300 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">漫画分类</h2>
            <p className="text-sm text-foreground-500 mt-2">按分类浏览，快速找到你喜欢的漫画类型</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer group"
              >
                <span className="w-10 h-10 flex items-center justify-center text-xl text-primary-500 group-hover:text-primary-600 transition-colors duration-200">
                  <i className={cat.icon}></i>
                </span>
                <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-700 transition-colors duration-200 whitespace-nowrap">{cat.name}</span>
                <span className="text-xs text-foreground-500 whitespace-nowrap">{cat.description}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Manga */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">热门推荐</h2>
              <p className="text-sm text-foreground-500 mt-1">评分最高的精选漫画</p>
            </div>
            <Link
              to="/category/rexue"
              className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              查看全部 <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
            {featuredManga.map((manga) => (
              <MangaCard key={manga.id} manga={manga} />
            ))}
          </div>
        </div>
      </section>

      {/* Latest Updates */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">最近更新</h2>
              <p className="text-sm text-foreground-500 mt-1">最新更新的漫画章节</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
            {latestManga.map((manga) => (
              <MangaCard key={manga.id} manga={manga} />
            ))}
          </div>
        </div>
      </section>

      {/* Popular Ranking */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">人气排行</h2>
              <p className="text-sm text-foreground-500 mt-1">阅读量最高的漫画</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
            {popularManga.map((manga, index) => (
              <div key={manga.id} className="relative">
                {index < 3 && (
                  <div className="absolute -top-2 -left-2 z-10 w-7 h-7 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-xs font-bold">
                    {index + 1}
                  </div>
                )}
                <MangaCard manga={manga} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">常见问题</h2>
            <p className="text-sm text-foreground-500 mt-2">关于木瓜漫画，你想知道的都在这里</p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group rounded-lg border border-background-200/70 bg-background-50 overflow-hidden"
              >
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors duration-200">
                  {faq.question}
                  <span className="w-5 h-5 flex items-center justify-center text-foreground-400 group-open:rotate-180 transition-transform duration-200">
                    <i className="ri-arrow-down-s-line"></i>
                  </span>
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: faqs.map((faq) => ({
                  '@type': 'Question',
                  name: faq.question,
                  acceptedAnswer: { '@type': 'Answer', text: faq.answer },
                })),
              }),
            }}
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 md:py-20 relative overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20abstract%20ink%20wash%20background%20with%20soft%20coral%20and%20peach%20gradients%2C%20flowing%20artistic%20strokes%2C%20Japanese%20sumi-e%20style%2C%20elegant%20and%20minimal%2C%20light%20cream%20base%20with%20warm%20accents&width=1800&height=600&seq=manga-cta-bg-02&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-500/90 to-accent-500/80"></div>
        <div className="relative z-10 max-w-2xl mx-auto text-center px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-background-50 font-heading mb-3">
            准备好开始你的漫画之旅了吗？
          </h2>
          <p className="text-sm md:text-base text-background-200 mb-6">
            加入500万+漫画爱好者，畅享海量正版漫画，完全免费！
          </p>
          <Link
            to="/category/rexue"
            className="inline-flex px-8 py-3 bg-background-50 text-primary-600 rounded-lg text-sm md:text-base font-semibold hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            立即免费阅读
          </Link>
        </div>
      </section>
    </>
  );
}